import { useEffect, useState } from "react";
import type React from "react";
import { theme, inputStyle, type as text } from "../theme";
import { fmtDateTime } from "../lib/format";

// Root cause for a corrective action (ISO 27001 10.2 b). The method is a choice; the finding is
// text. For five whys there is a small guided editor of five "Why?" lines that saves into the same
// text as numbered lines ("1. Why? ..."), so the stored value reads the same wherever it is shown
// (the evidence document, the AI context) and needs no second field. Whether the cause is required
// before completion is the server's rule (lib/correctiveActions.ts completionError).

export interface RootCauseValue { method: string; text: string }

const WHY_LINE = /^\s*\d+\.\s*Why\?\s*/i;

/** The five answers held in numbered text, or null when the text is not in that shape. */
export function parseFiveWhys(text: string): string[] | null {
  const trimmed = text.trim();
  if (!trimmed) return ["", "", "", "", ""];
  const lines = trimmed.split(/\r?\n/);
  if (!lines.every((l) => WHY_LINE.test(l)) || lines.length > 5) return null;
  const answers = lines.map((l) => l.replace(WHY_LINE, "").trim());
  while (answers.length < 5) answers.push("");
  return answers;
}

export function formatFiveWhys(answers: string[]): string {
  return answers.map((a) => a.trim()).filter(Boolean).map((a, i) => `${i + 1}. Why? ${a}`).join("\n");
}

const field: React.CSSProperties = { ...inputStyle, fontSize: text.body.fontSize };

/**
 * The recorded root cause as it reads in an action's view: the method, then the finding (five
 * whys as a numbered list when the text is in that shape). Editing is the form's, behind Edit.
 */
export function RootCauseView({ value, methods, required, aiDraftAt }: {
  value: RootCauseValue;
  methods: Array<{ id: string; label: string }>;
  required: boolean;
  /** When the saved root cause started from an AI draft the owner reviewed (backend lib/rootCauseDraft.ts). */
  aiDraftAt?: string | null;
}) {
  const methodLabel = methods.find((m) => m.id === value.method)?.label ?? (value.method ? value.method.replace(/_/g, " ") : null);
  const whys = value.method === "five_whys" && value.text.trim() ? parseFiveWhys(value.text) : null;
  return (
    <div>
      <div style={{ ...text.hint, marginBottom: 6 }}>
        Method: {methodLabel ?? "not chosen"}
        {required && (
          <span style={{ ...text.meta, fontWeight: 600, marginLeft: 8, color: value.text.trim() ? theme.color.successText : theme.color.warningText }}>
            {value.text.trim() ? "Recorded" : "Needed before this can be completed"}
          </span>
        )}
      </div>
      {!value.text.trim() ? (
        <div style={{ ...text.body, color: theme.color.faint }}>Not recorded</div>
      ) : whys ? (
        <ol style={{ ...text.body, margin: 0, paddingLeft: 20, display: "grid", gap: 4 }}>
          {whys.filter(Boolean).map((w, i) => <li key={i}>Why? {w}</li>)}
        </ol>
      ) : (
        <div style={{ ...text.body, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{value.text}</div>
      )}
      {aiDraftAt && value.text.trim() && (
        <div style={{ ...text.meta, marginTop: 6 }}>Started from an AI draft, reviewed and saved on {fmtDateTime(aiDraftAt)}.</div>
      )}
    </div>
  );
}

export function ActionRootCause({ value, onChange, methods, required }: {
  value: RootCauseValue;
  onChange: (v: RootCauseValue) => void;
  methods: Array<{ id: string; label: string }>;
  /** The action's source means something went wrong, so the cause is needed before completion. */
  required: boolean;
}) {
  // The guided editor keeps its own five lines so an answer can be blank mid-way without the
  // numbering collapsing under the cursor; the stored text is re-derived on every change.
  const parsed = value.method === "five_whys" ? parseFiveWhys(value.text) : null;
  const [whys, setWhys] = useState<string[]>(parsed ?? ["", "", "", "", ""]);
  const [freeText, setFreeText] = useState(value.method === "five_whys" && !parsed);

  useEffect(() => {
    if (value.method !== "five_whys") return;
    const p = parseFiveWhys(value.text);
    if (p && formatFiveWhys(p) !== formatFiveWhys(whys)) setWhys(p);
    // Only resync when the stored value changes from outside (a different action opened).
  }, [value.method, value.text]); // eslint-disable-line react-hooks/exhaustive-deps

  const guided = value.method === "five_whys" && !freeText;

  return (
    <div>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}>
        <select
          aria-label="Root cause method"
          value={value.method}
          onChange={(e) => {
            const method = e.target.value;
            const p = method === "five_whys" ? parseFiveWhys(value.text) : null;
            if (p) setWhys(p);
            setFreeText(method === "five_whys" && !p);
            onChange({ method, text: value.text });
          }}
          style={{ ...field, width: "auto" }}
        >
          <option value="">Method: not chosen</option>
          {methods.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
        </select>
        {required && (
          <span style={{ ...text.meta, fontWeight: 600, color: value.text.trim() ? theme.color.successText : theme.color.warningText }}>
            {value.text.trim() ? "Recorded" : "Needed before this can be completed"}
          </span>
        )}
        {value.method === "five_whys" && (
          <button
            type="button"
            onClick={() => {
              if (freeText) {
                const p = parseFiveWhys(value.text);
                if (!p) return;
                setWhys(p);
              }
              setFreeText(!freeText);
            }}
            disabled={freeText && !parseFiveWhys(value.text)}
            title={freeText && !parseFiveWhys(value.text) ? "The text is not in five-whys form, so it stays as free text" : undefined}
            style={{ fontSize: text.hint.fontSize, background: "transparent", border: "none", color: theme.color.accent, cursor: "pointer", padding: 0, fontFamily: theme.font }}
          >
            {freeText ? "Use the guided editor" : "Edit as text"}
          </button>
        )}
      </div>

      {guided ? (
        <ol style={{ margin: 0, paddingLeft: 20, display: "grid", gap: 6 }}>
          {whys.map((w, i) => (
            <li key={i} style={text.hint}>
              <input
                value={w}
                aria-label={`Why ${i + 1}`}
                placeholder={i === 0 ? "Why did it happen?" : "Why was that?"}
                onChange={(e) => {
                  const next = whys.map((x, j) => (j === i ? e.target.value : x));
                  setWhys(next);
                  onChange({ method: value.method, text: formatFiveWhys(next) });
                }}
                style={{ ...field, marginLeft: 4 }}
              />
            </li>
          ))}
        </ol>
      ) : (
        <textarea
          aria-label="Root cause"
          value={value.text}
          onChange={(e) => onChange({ method: value.method, text: e.target.value })}
          placeholder={value.method === "fishbone" ? "The contributing causes by category (people, process, technology, environment) and which one was decisive." : value.method === "fault_tree" ? "The top event, the combination of failures that led to it, and the basic cause." : "What caused it, so the fix removes the cause rather than the symptom."}
          style={{ ...field, resize: "vertical", minHeight: 70 }}
        />
      )}
    </div>
  );
}
